import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { insertWithOfflineFallback } from '../lib/offlineMutation';
import { createRealtimeSync, type RealtimeSync } from '../lib/realtime/createRealtimeSync';
import { logResyncFailure } from '../lib/realtime/logResync';
import type {
  CRMNote,
  CRMMeeting,
  MeetingType,
  MeetingStatus,
  NoteType,
  NoteSource,
  NoteAppointmentRef,
  CRMMeetingParticipant,
  CRMMeetingDocument,
  CreateNoteInput,
  UpdateNoteInput,
  CreateMeetingInput,
  NoteFilters,
  CRMNoteHistoryEntry,
  RestoreNoteVersionResult
} from '../types/notes';
import type { CRMContact, CRMAccount } from '../types/crm';
import type { Database, Json } from '../types/supabase';

type NoteRow = Database['public']['Tables']['crm_notes']['Row'];
type MeetingRow = Database['public']['Tables']['crm_meetings']['Row'];
type ContactRow = Database['public']['Tables']['crm_contacts']['Row'];
type AccountRow = Database['public']['Tables']['crm_accounts']['Row'];

/** The contacts query joins the lightweight parent account (id + name only). */
type ContactRowJoined = ContactRow & {
  account?: { id: string; name: string } | null;
};

/** Narrow a nullable JSON column into a plain domain Record. */
function toRecord(value: Json | null | undefined): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Normalize a joined `crm_contacts` DB row into the domain `CRMContact`.
 * The DB stores `status` as a free-form string and keeps a few flags nullable;
 * they are narrowed here to the canonical `active`/`inactive` union.
 */
function toCRMContact(row: ContactRowJoined): CRMContact {
  return {
    id: row.id,
    account_id: row.account_id,
    first_name: row.first_name,
    last_name: row.last_name,
    email: row.email,
    phone: row.phone,
    title: row.title,
    status: (row.status as CRMContact['status']) ?? 'active',
    owner_id: row.owner_id,
    last_contacted_at: row.last_contacted_at,
    feature_notifications_enabled: row.feature_notifications_enabled ?? false,
    custom_fields: toRecord(row.custom_fields),
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/**
 * Normalize a `crm_accounts` DB row into the domain `CRMAccount`.
 * The commercial `role`/`status` are derived from the revenue view elsewhere
 * (see accountsSlice); rows loaded here have no such context, so they default
 * to the neutral `prospect` role.
 */
function toCRMAccount(row: AccountRow): CRMAccount {
  return {
    id: row.id,
    company_id: row.company_id,
    name: row.name,
    industry: row.industry,
    website: row.website,
    logo_url: row.logo_url,
    address: row.address,
    city: row.city,
    postal_code: row.postal_code,
    country: row.country,
    siret: row.siret,
    siren: row.siren,
    naf_code: row.naf_code,
    phone: row.phone,
    role: 'prospect',
    status: 'prospect',
    notes: row.notes,
    owner_id: row.owner_id,
    parent_account_id: row.parent_account_id,
    custom_fields: toRecord(row.custom_fields),
    lat: row.lat,
    lng: row.lng,
    geocoded_at: row.geocoded_at,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** The notes query joins several lightweight related rows. */
type NoteRowJoined = NoteRow & {
  account?: CRMNote['account'];
  contact?: CRMNote['contact'];
  meeting?: CRMNote['meeting'];
  opportunity?: CRMNote['opportunity'];
  lead?: CRMNote['lead'];
  appointment?: NoteAppointmentRef | null;
};

type MeetingParticipantRow = Database['public']['Tables']['crm_meeting_participants']['Row'];
type MeetingDocumentRow = Database['public']['Tables']['crm_meeting_documents']['Row'];

/**
 * The meetings query joins the account (id + name), the participants (with a
 * light contact join) and the full document rows. These join shapes are looser
 * than the domain relation types; `toCRMMeeting` narrows them.
 */
type MeetingParticipantJoined = Pick<MeetingParticipantRow, 'id' | 'meeting_id' | 'contact_id' | 'role' | 'created_at'> & {
  contact?: { id: string; first_name: string; last_name: string; email: string | null } | null;
};
type MeetingRowJoined = MeetingRow & {
  account?: { id: string; name: string } | null;
  participants?: MeetingParticipantJoined[];
  documents?: MeetingDocumentRow[];
};

/**
 * Normalize a joined `crm_notes` DB row into the domain `CRMNote`.
 * The DB keeps `tags`/`created_at`/`updated_at`/`is_pinned` nullable and stores
 * `source` as a free-form string; they are narrowed here to the domain shape.
 */
function toCRMNote(row: NoteRowJoined): CRMNote {
  return {
    id: row.id,
    title: row.title,
    content: row.content,
    note_type: row.note_type as NoteType,
    account_id: row.account_id,
    contact_id: row.contact_id,
    meeting_id: row.meeting_id,
    appointment_id: row.appointment_id,
    opportunity_id: row.opportunity_id,
    lead_id: row.lead_id,
    tags: row.tags ?? [],
    is_pinned: row.is_pinned ?? false,
    owner_id: row.owner_id,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
    source: row.source as NoteSource,
    account: row.account ?? null,
    contact: row.contact ?? null,
    meeting: row.meeting ?? null,
    opportunity: row.opportunity ?? null,
    lead: row.lead ?? null,
    appointment: row.appointment ?? null,
  };
}

/**
 * Normalize a joined `crm_meetings` DB row into the domain `CRMMeeting`.
 * `created_at`/`updated_at` are nullable in the DB but required by the domain
 * type, and `meeting_type`/`status` are narrowed to their canonical unions.
 */
function toCRMMeeting(row: MeetingRowJoined): CRMMeeting {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    meeting_type: row.meeting_type as MeetingType,
    scheduled_at: row.scheduled_at,
    duration_minutes: row.duration_minutes,
    location: row.location,
    google_meet_link: row.google_meet_link,
    google_calendar_event_id: row.google_calendar_event_id,
    status: row.status as MeetingStatus,
    account_id: row.account_id,
    owner_id: row.owner_id,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
    // The query joins only `{ id, name }`; the domain relation is wider but the
    // UI reads just those fields, so the lightweight join is kept as-is.
    account: (row.account ?? null) as CRMMeeting['account'],
    participants: row.participants?.map(toCRMMeetingParticipant),
    documents: row.documents?.map(toCRMMeetingDocument),
  };
}

/** Normalize a joined `crm_meeting_participants` row into the domain type. */
function toCRMMeetingParticipant(row: MeetingParticipantJoined): CRMMeetingParticipant {
  return {
    id: row.id,
    meeting_id: row.meeting_id,
    contact_id: row.contact_id,
    role: row.role ?? '',
    created_at: row.created_at ?? '',
    contact: row.contact
      ? ({
          id: row.contact.id,
          first_name: row.contact.first_name,
          last_name: row.contact.last_name,
          email: row.contact.email,
        } as CRMContact)
      : undefined,
  };
}

/** Normalize a `crm_meeting_documents` row into the domain type. */
function toCRMMeetingDocument(row: MeetingDocumentRow): CRMMeetingDocument {
  return {
    id: row.id,
    meeting_id: row.meeting_id,
    document_type: row.document_type,
    title: row.title,
    content: row.content,
    file_url: row.file_url,
    source: row.source ?? '',
    metadata: toRecord(row.metadata),
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}

interface NotesState {
  notes: CRMNote[];
  meetings: CRMMeeting[];
  recentContacts: CRMContact[];
  recentAccounts: CRMAccount[];
  loading: boolean;
  error: string | null;
  filters: NoteFilters;

  fetchNotes: (filters?: NoteFilters, opts?: { silent?: boolean }) => Promise<void>;
  /** One note with the same joins as the list, regardless of the active filters (deep links). */
  fetchNoteById: (id: string) => Promise<CRMNote | null>;
  fetchMeetings: (opts?: { silent?: boolean }) => Promise<void>;
  fetchRecentContacts: () => Promise<void>;
  fetchRecentAccounts: () => Promise<void>;

  createNote: (input: CreateNoteInput) => Promise<CRMNote | null>;
  updateNote: (id: string, input: UpdateNoteInput) => Promise<CRMNote | null>;
  deleteNote: (id: string) => Promise<boolean>;
  togglePinNote: (id: string) => Promise<boolean>;

  createMeeting: (input: CreateMeetingInput) => Promise<CRMMeeting | null>;
  updateMeetingStatus: (id: string, status: CRMMeeting['status']) => Promise<boolean>;

  searchContacts: (query: string) => Promise<CRMContact[]>;
  searchAccounts: (query: string) => Promise<CRMAccount[]>;

  selectedNoteId: string | null;
  setSelectedNoteId: (id: string | null) => void;

  setFilters: (filters: NoteFilters) => void;
  clearError: () => void;

  fetchNoteVersions: (noteId: string) => Promise<CRMNoteHistoryEntry[]>;
  restoreNoteVersion: (noteId: string, versionId: string) => Promise<RestoreNoteVersionResult>;
  getNoteVersionContent: (versionId: string) => Promise<CRMNoteHistoryEntry | null>;
}

/** Live subscription for the CRM notes & meetings surface. */
let notesSync: RealtimeSync | null = null;

/**
 * Monotonic guard so a stale/concurrent `fetchNotes` can't clobber `notes`.
 *
 * Notes are re-fetched from several places at once — mount (page + sidebar),
 * and the `rt-notes` Realtime sync (catch-up on connect + a silent refetch on
 * every crm_notes change). A refetch that *started* before a freshly-created
 * note was visible would resolve later and overwrite the optimistic insert,
 * dropping the new note from the list (editor stays open via justCreatedNote,
 * but the list loses it). `createNote` bumps this counter so any in-flight
 * fetch is discarded when it resolves; each fetch ignores its own result if a
 * newer fetch or mutation has since superseded it.
 */
let notesFetchSeq = 0;

/** Lazily open the notes/meetings Realtime subscription (idempotent). */
/** Joins carried by every note shown in the editor (list and single fetch alike). */
const NOTE_LIST_SELECT = `
  *,
  account:crm_accounts(id, name),
  contact:crm_contacts(id, first_name, last_name, email),
  meeting:crm_meetings(id, title, scheduled_at),
  opportunity:crm_opportunities(id, name),
  lead:onboarding_leads(id, company_name, contact_first_name, contact_last_name),
  appointment:appointments!crm_notes_appointment_id_fkey(id, title, start_time)
`;

function ensureNotesRealtime(get: () => NotesState): void {
  if (!notesSync) {
    notesSync = createRealtimeSync({
      channelName: 'rt-notes',
      tables: ['crm_notes', 'crm_meetings', 'crm_meeting_participants'],
      onChange: async () => {
        await Promise.all([
          get().fetchNotes(undefined, { silent: true }),
          get().fetchMeetings({ silent: true }),
        ]);
      },
    });
  }
  notesSync.subscribe();
}

export const useNotesStore = create<NotesState>((set, get) => ({
  notes: [],
  meetings: [],
  recentContacts: [],
  recentAccounts: [],
  loading: false,
  error: null,
  // `source: 'manual'` masque par défaut les notes des agents Otto (revue de prospect).
  filters: { note_type: 'all', source: 'manual' },
  selectedNoteId: null,
  setSelectedNoteId: (id) => set({ selectedNoteId: id }),

  fetchNotes: async (filters?: NoteFilters, opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    const seq = ++notesFetchSeq;
    if (!silent) set({ loading: true, error: null });
    try {
      const activeFilters = filters || get().filters;

      // Si recherche active, on passe par la RPC enrichie (FTS + tags + entites liees)
      // pour recuperer les IDs eligibles, puis on fait le SELECT classique.
      let matchingIds: string[] | null = null;
      if (activeFilters.search && activeFilters.search.trim().length > 0) {
        const { data: ids, error: rpcError } = await supabase.rpc('search_crm_notes', {
          p_query: activeFilters.search.trim(),
        });
        if (rpcError) throw rpcError;
        matchingIds = (ids || []).map((row: { note_id: string }) => row.note_id);
        if (matchingIds.length === 0) {
          if (seq === notesFetchSeq) set({ notes: [], ...(silent ? {} : { loading: false }) });
          return;
        }
      }

      let query = supabase
        .from('crm_notes')
        .select(NOTE_LIST_SELECT)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (matchingIds !== null) {
        query = query.in('id', matchingIds);
      }
      if (activeFilters.note_type && activeFilters.note_type !== 'all') {
        query = query.eq('note_type', activeFilters.note_type);
      }
      if (activeFilters.source && activeFilters.source !== 'all') {
        query = query.eq('source', activeFilters.source);
      }
      if (activeFilters.account_id) {
        query = query.eq('account_id', activeFilters.account_id);
      }
      if (activeFilters.contact_id) {
        query = query.eq('contact_id', activeFilters.contact_id);
      }
      if (activeFilters.is_pinned !== undefined) {
        query = query.eq('is_pinned', activeFilters.is_pinned);
      }
      if (activeFilters.date_from) {
        query = query.gte('created_at', activeFilters.date_from);
      }
      if (activeFilters.date_to) {
        query = query.lte('created_at', activeFilters.date_to);
      }

      const { data, error } = await query;

      if (error) throw error;
      // Drop this result if a newer fetch or a mutation (createNote) has since
      // superseded it — otherwise a slow refetch would clobber the live list.
      if (seq === notesFetchSeq) {
        const notes = ((data ?? []) as NoteRowJoined[]).map(toCRMNote);
        set({ notes, ...(silent ? {} : { loading: false }) });
      }
      ensureNotesRealtime(get);
    } catch (err) {
      const message = (err as Error).message;
      if (silent) logResyncFailure('notes resync failed', err);
      else set({ error: message, loading: false });
    }
  },

  fetchMeetings: async (opts) => {
    const silent = opts?.silent ?? false;
    if (!silent) set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('crm_meetings')
        .select(`
          *,
          account:crm_accounts(id, name),
          participants:crm_meeting_participants(
            id,
            role,
            contact:crm_contacts(id, first_name, last_name, email)
          ),
          documents:crm_meeting_documents(*)
        `)
        .order('scheduled_at', { ascending: false });

      if (error) throw error;
      const meetings = ((data ?? []) as MeetingRowJoined[]).map(toCRMMeeting);
      set({ meetings, ...(silent ? {} : { loading: false }) });
      ensureNotesRealtime(get);
    } catch (err) {
      const message = (err as Error).message;
      if (silent) logResyncFailure('meetings resync failed', err);
      else set({ error: message, loading: false });
    }
  },

  fetchRecentContacts: async () => {
    try {
      const { data } = await supabase
        .from('crm_contacts')
        .select('*, account:crm_accounts(id, name)')
        .order('last_contacted_at', { ascending: false, nullsFirst: false })
        .limit(10);

      set({ recentContacts: ((data ?? []) as ContactRowJoined[]).map(toCRMContact) });
    } catch {
      // Silent fail for recent contacts
    }
  },

  fetchRecentAccounts: async () => {
    try {
      const { data } = await supabase
        .from('crm_accounts')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(10);

      set({ recentAccounts: ((data ?? []) as AccountRow[]).map(toCRMAccount) });
    } catch {
      // Silent fail for recent accounts
    }
  },

  fetchNoteById: async (id: string) => {
    const { data, error } = await supabase
      .from('crm_notes')
      .select(NOTE_LIST_SELECT)
      .eq('id', id)
      .maybeSingle();
    if (error) {
      set({ error: error.message });
      return null;
    }
    return data ? toCRMNote(data as NoteRowJoined) : null;
  },

  createNote: async (input: CreateNoteInput) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Non authentifie');

      const payload = {
        ...input,
        owner_id: userData.user.id,
        tags: input.tags || [],
        is_pinned: input.is_pinned || false,
        note_type: input.note_type || 'general',
      };

      // Mutation tolérante offline (queue + replay automatique)
      const result = await insertWithOfflineFallback<CRMNote>({
        table: 'crm_notes',
        payload,
        select:
          '*, account:crm_accounts(id, name), contact:crm_contacts(id, first_name, last_name, email)',
      });

      if (!result.ok) {
        set({ error: result.error, loading: false });
        return null;
      }

      // Cas offline : on injecte un optimistic record local en attendant le replay.
      // Au prochain `flushOfflineQueue`, l'insert réel se fera (UNIQUE client_request_id
      // garantit l'idempotency).
      const optimistic: CRMNote =
        result.queued
          ? ({
              ...(payload as object),
              id: result.clientRequestId,
              client_request_id: result.clientRequestId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              account: null,
              contact: null,
              _offline: true,
            } as unknown as CRMNote)
          : result.data;

      if (input.contact_id && !result.queued) {
        await supabase
          .from('crm_contacts')
          .update({ last_contacted_at: new Date().toISOString() })
          .eq('id', input.contact_id);
      }

      // Invalidate any in-flight fetchNotes so it can't resolve later and drop
      // this fresh note from the list (the long-standing "new note vanishes"
      // bug). Subsequent fetches get a higher seq and read the committed row.
      notesFetchSeq++;
      set((state) => ({
        notes: [optimistic, ...state.notes],
        loading: false,
      }));

      return optimistic;
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      return null;
    }
  },

  updateNote: async (id: string, input: UpdateNoteInput) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('crm_notes')
        .update(input)
        .eq('id', id)
        .select(`
          *,
          account:crm_accounts(id, name),
          contact:crm_contacts(id, first_name, last_name, email)
        `)
        .single();

      if (error) throw error;

      const note = toCRMNote(data as NoteRowJoined);
      set((state) => ({
        notes: state.notes.map((n) => (n.id === id ? note : n)),
        loading: false,
      }));

      return note;
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      return null;
    }
  },

  deleteNote: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('crm_notes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        notes: state.notes.filter((n) => n.id !== id),
        loading: false,
      }));

      return true;
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      return false;
    }
  },

  togglePinNote: async (id: string) => {
    const note = get().notes.find((n) => n.id === id);
    if (!note) return false;

    try {
      const { error } = await supabase
        .from('crm_notes')
        .update({ is_pinned: !note.is_pinned })
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        notes: state.notes.map((n) =>
          n.id === id ? { ...n, is_pinned: !n.is_pinned } : n
        ),
      }));

      return true;
    } catch {
      return false;
    }
  },

  createMeeting: async (input: CreateMeetingInput) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Non authentifie');

      const { contact_ids, ...meetingData } = input;

      const { data, error } = await supabase
        .from('crm_meetings')
        .insert({
          ...meetingData,
          owner_id: userData.user.id,
          meeting_type: input.meeting_type || 'call',
          status: 'scheduled',
        })
        .select()
        .single();

      if (error) throw error;

      if (contact_ids && contact_ids.length > 0) {
        const participants = contact_ids.map((contact_id) => ({
          meeting_id: data.id,
          contact_id,
          role: 'attendee',
        }));

        await supabase.from('crm_meeting_participants').insert(participants);
      }

      const meeting = toCRMMeeting(data);
      set((state) => ({
        meetings: [meeting, ...state.meetings],
        loading: false,
      }));

      return meeting;
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      return null;
    }
  },

  updateMeetingStatus: async (id: string, status: CRMMeeting['status']) => {
    try {
      const { error } = await supabase
        .from('crm_meetings')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        meetings: state.meetings.map((m) =>
          m.id === id ? { ...m, status } : m
        ),
      }));

      return true;
    } catch {
      return false;
    }
  },

  searchContacts: async (query: string) => {
    if (query.length < 2) return [];

    try {
      const { data } = await supabase
        .from('crm_contacts')
        .select('*, account:crm_accounts(id, name)')
        .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,email.ilike.%${query}%`)
        .limit(10);

      return ((data ?? []) as ContactRowJoined[]).map(toCRMContact);
    } catch {
      return [];
    }
  },

  searchAccounts: async (query: string) => {
    if (query.length < 2) return [];

    try {
      const { data } = await supabase
        .from('crm_accounts')
        .select('*')
        .ilike('name', `%${query}%`)
        .limit(10);

      return ((data ?? []) as AccountRow[]).map(toCRMAccount);
    } catch {
      return [];
    }
  },

  setFilters: (filters: NoteFilters) => {
    set({ filters });
    get().fetchNotes(filters);
  },

  clearError: () => set({ error: null }),

  fetchNoteVersions: async (noteId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_crm_note_versions', {
        p_note_id: noteId
      });

      if (error) throw error;
      return (data || []) as CRMNoteHistoryEntry[];
    } catch (err) {
      console.error('Error fetching note versions:', err);
      return [];
    }
  },

  restoreNoteVersion: async (noteId: string, versionId: string) => {
    set({ loading: true });
    try {
      const { data, error } = await supabase.rpc('restore_crm_note_version', {
        p_note_id: noteId,
        p_version_id: versionId
      });

      if (error) throw error;

      // The RPC returns an opaque JSON payload shaped as RestoreNoteVersionResult.
      const result = data as unknown as RestoreNoteVersionResult;
      if (result.success && result.note) {
        set((state) => ({
          notes: state.notes.map((n) =>
            n.id === noteId ? { ...n, ...result.note } as CRMNote : n
          ),
        }));
      }

      return result;
    } catch (err) {
      return { success: false, error: (err as Error).message };
    } finally {
      set({ loading: false });
    }
  },

  getNoteVersionContent: async (versionId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_crm_note_version_content', {
        p_version_id: versionId
      });

      if (error) throw error;

      // The RPC returns an opaque JSON payload: either an error envelope or a
      // note-history entry shaped as CRMNoteHistoryEntry.
      const payload = data as unknown as (CRMNoteHistoryEntry & { error?: string }) | null;
      if (payload?.error) {
        return null;
      }

      return payload as CRMNoteHistoryEntry;
    } catch (err) {
      console.error('Error fetching note version content:', err);
      return null;
    }
  },
}));
